import { enumerateAudioDevices } from './audioDevices'

/**
 * What this platform can do for the softphone, decided by feature detection and nothing else.
 *
 * Never by user agent: a platform is identified by what it lacks, so a browser that ships a missing
 * API starts getting the control the day it does, and one that pretends to be something else is not
 * offered a control it cannot honour. Every control that depends on platform support reads its
 * answer from here, so there is one place to look when a control is missing on some device.
 *
 * Built to grow: wake lock, Media Session and standalone display mode are expected to land here for
 * the mobile work.
 */

/**
 * What "a phone" means to the softphone: a screen whose primary input is a finger, with nothing that
 * can hover. A tablet held in the hand reads the same, and should. A desktop does not, and neither
 * does a touch laptop with a mouse or trackpad, whose primary pointer is the fine one.
 *
 * Asked each time rather than once at boot, so the answer is the one that holds at the moment it
 * decides something. False where the browser cannot say, which keeps the desktop behaviour.
 */
export function isTouchOnlyDevice() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: none) and (pointer: coarse)').matches === true
}

/**
 * Output routing is `setSinkId` on media elements. iOS Safari has none, so a speaker select there
 * would change nothing - and that absence is the whole answer: no enumeration result can make up
 * for it.
 */
export function supportsOutputRouting() {
  return typeof HTMLMediaElement !== 'undefined'
    && typeof HTMLMediaElement.prototype.setSinkId === 'function'
}

/**
 * The full answer, which needs an enumeration: Chrome on Android implements `setSinkId` but lists no
 * audio outputs, so there is nothing to choose between. Outputs do not enumerate reliably before
 * microphone permission is granted either, which is why the panel calls this again once it is.
 *
 * Inputs are gated on their own, so a platform that lists microphones but not speakers still offers
 * a microphone choice.
 */
export async function readAudioDeviceCapabilities() {
  const devices = await enumerateAudioDevices()

  const hasOutputs = devices.some(device => device.kind === 'audiooutput')
  const hasInputs = devices.some(device => device.kind === 'audioinput')

  return {
    canSelectInput: hasInputs,
    canSelectOutput: supportsOutputRouting() && hasOutputs
  }
}
